import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DexaImport from "@/components/DexaImport";
import { Dumbbell } from "lucide-react";

function Spark({ points, dir }) {
  if (!points || points.length < 2) return <span className="text-muted-foreground">-</span>;
  const vals = points.map((p) => p.value);
  const min = Math.min(...vals), max = Math.max(...vals);
  const rng = max - min || 1;
  const w = 84, h = 22;
  const d = points.map((p, i) => `${(i / (points.length - 1)) * w},${h - ((p.value - min) / rng) * h}`).join(" ");
  const improved = (dir === "up" ? points.at(-1).value > points[0].value : points.at(-1).value < points[0].value);
  return (
    <svg width={w} height={h}>
      <polyline points={d} fill="none" strokeWidth="1.5" className={improved ? "stroke-traffic-green" : "stroke-traffic-red"} />
    </svg>
  );
}

/** Body composition tab · DEXA-driven activity-style table with delta vs baseline and history sparkline. */
export default function BodyComposition({ athleteId }) {
  const { user } = useAuth();
  const isStaff = user?.role !== "athlete";
  const [pt, setPt] = useState(null);

  const load = () => api.get(`/athletes/${athleteId}/population-target`).then((r) => setPt(r.data)).catch(() => {});
  useEffect(() => { if (athleteId) load(); /* eslint-disable-next-line */ }, [athleteId]);

  if (!pt) return null;
  const rows = pt.body_composition || [];
  return (
    <div className="border border-border" data-testid="section-body_composition">
      <div className="px-5 py-4 border-b border-border flex items-center gap-3 flex-wrap">
        <h3 className="font-heading font-bold tracking-tight flex items-center gap-2">
          <Dumbbell className="w-4 h-4" /> Body composition
          <span data-testid="body-composition-source" className="text-xs text-muted-foreground font-normal">· {pt.body_composition_source}{pt.dexa_as_of ? ` · latest ${pt.dexa_as_of}` : ""} · non-medical performance context</span>
        </h3>
      </div>
      <div className="overflow-x-auto">
        {rows.length === 0 ? (
          <p className="p-5 text-sm text-muted-foreground">No DEXA data yet · upload a scan or enter values to add body-composition context.</p>
        ) : (
          <table className="w-full text-sm" data-testid="body-comp-table">
            <thead>
              <tr className="border-b border-border text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">
                <th className="text-left p-3">Measure</th>
                <th className="text-left p-3">Trend</th>
                <th className="text-right p-3">Latest</th>
                <th className="text-right p-3">Baseline</th>
                <th className="text-right p-3">Δ base</th>
                <th className="text-right p-3">Target</th>
                <th className="text-right p-3">Squad avg</th>
                <th className="text-right p-3">Readings</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => {
                const better = b.direction === "up" ? (b.delta ?? 0) > 0 : (b.delta ?? 0) < 0;
                return (
                  <tr key={b.key} className="border-b border-border last:border-0" data-testid={`bc-row-${b.key}`}>
                    <td className="p-3 font-medium">{b.label}{b.unit ? <span className="text-muted-foreground"> ({b.unit})</span> : null}</td>
                    <td className="p-3"><Spark points={b.points} dir={b.direction} /></td>
                    <td className="p-3 text-right tabular-nums font-heading font-bold">{b.value}</td>
                    <td className="p-3 text-right tabular-nums text-muted-foreground">{b.baseline ?? "-"}</td>
                    <td className="p-3 text-right">
                      {b.delta == null || b.readings < 2 ? <span className="text-muted-foreground">-</span> :
                        <span className={`text-xs px-1.5 py-0.5 border tabular-nums ${better ? "border-traffic-green text-traffic-green" : "border-traffic-red text-traffic-red"}`}>
                          {b.delta > 0 ? "+" : ""}{b.delta}{b.delta_pct != null ? ` (${b.delta_pct > 0 ? "+" : ""}${b.delta_pct}%)` : ""}
                        </span>}
                    </td>
                    <td className="p-3 text-right tabular-nums text-muted-foreground">{b.target ?? "-"}</td>
                    <td className="p-3 text-right tabular-nums text-muted-foreground">{b.population ?? "-"}</td>
                    <td className="p-3 text-right tabular-nums text-muted-foreground">{b.readings ?? 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {isStaff && (
        <div className="px-5 py-4 border-t border-border">
          <DexaImport athleteId={athleteId}
            current={Object.fromEntries(rows.map((b) => [b.key, b.value]))}
            onSaved={load} />
        </div>
      )}
    </div>
  );
}
